import type mapboxgl from 'mapbox-gl'

import type { SmartSearchOperationalHit } from '../types'

import { flyMapToFeature } from './flyToFeature'
import { removeSmartSearchNavigationPin } from './smartSearchNavigationPin'
import {
  showSmartSearchHitPopup,
  type ThemeContextValue,
} from './smartSearchHitPopup'

export type OperationalLayerVisibilitySnapshot = Map<
  string,
  'visible' | 'none'
>

function forEachOperationalLayerId(
  map: mapboxgl.Map,
  operationalLayerId: number,
  fn: (layerId: string) => void,
): void {
  const prefix = `operational-layer-${operationalLayerId}-`
  const layers = map.getStyle()?.layers ?? []
  for (const layer of layers) {
    if (!layer.id.startsWith(prefix)) continue
    fn(layer.id)
  }
}

export function snapshotOperationalLayerVisibility(
  map: mapboxgl.Map,
  operationalLayerId: number,
): OperationalLayerVisibilitySnapshot {
  const snap: OperationalLayerVisibilitySnapshot = new Map()
  forEachOperationalLayerId(map, operationalLayerId, layerId => {
    try {
      const vis = map.getLayoutProperty(layerId, 'visibility')
      snap.set(layerId, vis === 'none' ? 'none' : 'visible')
    } catch (err) {
      console.warn(
        '[smart-search] Could not read visibility for snapshot',
        layerId,
        err,
      )
    }
  })
  return snap
}

export function restoreOperationalLayerVisibility(
  map: mapboxgl.Map,
  snapshot: OperationalLayerVisibilitySnapshot,
): void {
  snapshot.forEach((visibility, layerId) => {
    try {
      map.setLayoutProperty(layerId, 'visibility', visibility)
    } catch (err) {
      console.warn(
        '[smart-search] Could not restore layer visibility',
        layerId,
        err,
      )
    }
  })
}

function ensureOperationalLayerVisible(
  map: mapboxgl.Map,
  operationalLayerId: number,
): void {
  forEachOperationalLayerId(map, operationalLayerId, layerId => {
    try {
      map.setLayoutProperty(layerId, 'visibility', 'visible')
    } catch (err) {
      console.warn('[smart-search] Could not show layer', layerId, err)
    }
  })
}

export function focusSmartSearchHit(
  map: mapboxgl.Map,
  hit: SmartSearchOperationalHit,
  // La bulle est rendue par React dans une racine detachee : le theme doit lui
  // etre passe, il n'est pas atteignable depuis cette couche imperative.
  themeCtx?: ThemeContextValue,
  propertiesOrder?: string[] | null,
): void {
  const run = () => {
    removeSmartSearchNavigationPin()
    try {
      ensureOperationalLayerVisible(map, hit.layerId)
      flyMapToFeature(map, hit.feature)
    } catch (err) {
      console.warn('[smart-search] focusSmartSearchHit failed', err)
      flyMapToFeature(map, hit.feature)
    }
    showSmartSearchHitPopup(map, hit, themeCtx, propertiesOrder)
  }

  if (!map.isStyleLoaded()) {
    map.once('style.load', run)
    return
  }

  run()
}
